import { Capacitor } from "@capacitor/core";
import {
  urlBase64ToUint8Array,
  PUBLIC_KEY,
} from "../serviceWorkerRegistration";
import { postNotificationsSubscription, fetchUnreadCount, markRecipientsReadByDelivery, refreshProfileAfterActivation } from "../redux/actions";
import {
  updateActiveNotifications,
  showPopup,
  incrementRecipientsUnreadCount,
} from "../redux/reducer/NotificationsSlice";
import store from "../redux/store";
import { PushNotifications } from "@capacitor/push-notifications";
import { syncPushNotificationDeviceAndTopics } from "../redux/actions/profilePushNotificationsActions";
import { isExternalActionUrl, openExternalUrl } from "./notificationActions";
import { getAuthToken } from "./authStorage";
import { markPushNavigation } from "./pushNavigationClock";

// Buffer for a navigation target produced by a notification tap. On a cold start
// (app killed → tap), `pushNotificationActionPerformed` can fire before any
// `push:navigate` listener is attached, so the CustomEvent would be lost. We also
// stash the path here and let App.js replay it once its listener mounts.
let pendingPushNavigationPath = null;
export const setPendingPushNavigation = (path) => {
  pendingPushNavigationPath = path;
  // Lets RedirectToHome know not to send the app to /home on top of it.
  markPushNavigation();
};
export const consumePendingPushNavigation = () => {
  const path = pendingPushNavigationPath;
  pendingPushNavigationPath = null;
  return path;
};

const cidFromData = (data = {}) =>
  data.cid ||
  (data.channel_type && data.channel_id
    ? `${data.channel_type}:${data.channel_id}`
    : data.channel_id || null);

// Data-only push the backend sends whenever a user's subscription changes
// (CRM activation, Stripe/RC webhook, admin grant). It carries no visible
// title/body — the app just refetches the profile so premium menu/recipe gates
// unlock in place, without a logout or app restart. Contract (agree with BE):
//   data.type === "subscription_updated"   (primary; aliases tolerated)
const isSubscriptionUpdatePush = (data = {}) => {
  const t = String(data.type || data.event || "").toLowerCase();
  return (
    t === "subscription_updated" ||
    t === "subscription_changed" ||
    data.subscription_updated === true ||
    data.subscription_updated === "true" ||
    data.subscription_updated === "1"
  );
};

// Convert a push deep-link (absolute URL or path) to an internal router path,
// mirroring the Centro-notifiche rewrite. A 3-segment comment link
// (/gruppi/{g}/{p}/{c}) is left AS-IS so it hits the CommentThreadDeepLink route
// — which opens the comments sheet and scrolls/expands to that exact
// comment/reply. Only the 2-segment post link is flattened to the feed's
// ?focusPost= scroll form (/gruppi/{g}?focusPost={p}).
/**
 * Backend link → in-app route. Handles both an absolute URL (CRM templates send
 * those) and a bare path (the social notifications send `/gruppi/{g}/{p}`).
 *
 * Exported because the Centro notifiche page needs exactly the same mapping:
 * it used to carry its own copy that only rewrote the absolute form, so a
 * relative like/post link was pushed verbatim and the feed opened without
 * focusing the post.
 */
export const pushLinkToInternalPath = (link) => {
  const rewriteGruppi = (p) => {
    const m = p.match(/^\/gruppi\/(\d+)\/(\d+)$/);
    if (m) return `/gruppi/${m[1]}?focusPost=${m[2]}`;
    return p;
  };
  if (!link) return "/home";
  try {
    const url = new URL(link);
    const p = rewriteGruppi(url.pathname) + url.search + url.hash;
    return p && p !== "/" ? p : "/home";
  } catch {
    const p = rewriteGruppi(link);
    return p.startsWith("/") ? p : `/${p}`;
  }
};

/**
 * Remove any OS notifications already delivered for a given Stream Chat channel.
 * Used to suppress the banner for the chat the user is currently viewing (and to
 * clear a channel's backlog when they open it). Native only.
 */
export const clearDeliveredChatNotifications = async (cid) => {
  if (!cid || !Capacitor.isNativePlatform()) return;
  try {
    const delivered = await PushNotifications.getDeliveredNotifications();
    const matches = (delivered?.notifications || []).filter(
      (n) => cidFromData(n?.data || {}) === cid,
    );
    if (matches.length) {
      await PushNotifications.removeDeliveredNotifications({
        notifications: matches,
      });
    }
  } catch {
    // best-effort — ignore
  }
};

class PushNotificationService {
  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.hasPermission = false;
    this.subscription = null;
    this.platform = this.detectPlatform();
    this.listeners = [];

  }

  /**
   * Detect the actual platform being used
   */
  detectPlatform() {
    // If we're in a native app, ALWAYS use native push notifications
    // This is the key fix - even if service workers are available in WebView
    if (this.isNative) {
      return "native";
    }

    // If we're in a browser with service workers, use PWA
    if ("serviceWorker" in navigator) {
      return "pwa";
    }

    return "unsupported";
  }

  get isPWA() {
    return this.platform === "pwa";
  }

  /**
   * Initialize push notifications based on platform
   */
  async initialize(opts = { prompt: true }) {
    if (this.platform === "native") {
      return this.initializeNative(opts);
    } else if (this.platform === "pwa") {
      return this.initializePWA();
    } else {
      console.warn("Push notifications not supported on this platform");
      return false;
    }
  }

  /**
   * Initialize native push notifications using Capacitor
   */
  async initializeNative({ prompt = true } = {}) {
    try {
      // Request permission
      const perm = prompt
        ? await PushNotifications.requestPermissions()
        : await PushNotifications.checkPermissions();
      const receive = perm?.receive ?? "denied";

      if (receive === "granted") {
        this.hasPermission = true;

        // Set up listeners
        await this.setupNativeListeners();

        // Register for push notifications
        try {
          // ⚠️ BEFORE register(), not after. Registration is what produces the
          // token the server pushes to, so creating the channel afterwards
          // leaves a window where a push can arrive with no channel to land on.
          //
          // The id must match the backend
          // (FirebasePushNotificationService::ANDROID_CHANNEL_ID) and the
          // manifest's default_notification_channel_id. They disagreed until
          // 2026-08-28 — the backend asked for "default", which nothing ever
          // created, so Android used its own default channel at NORMAL
          // importance and every push arrived silently in the tray instead of
          // as a heads-up banner.
          // Importance: 1=MIN, 2=LOW, 3=DEFAULT, 4=HIGH (heads-up), 5=MAX
          try {
            await PushNotifications.createChannel({
              id: "high-priority-notifications",
              name: "Notifiche importanti",
              description: "Notifiche push di Dieta Social",
              importance: 4, // IMPORTANCE_HIGH — enables heads-up popup
              visibility: 1, // PUBLIC
              sound: "default",
              vibration: true,
            });
          } catch (channelErr) {
            // createChannel is Android-only; silently ignore on iOS/web
            void channelErr;
          }

          await PushNotifications.register();

          // Update Redux state
          store.dispatch(updateActiveNotifications("granted"));
        } catch (error) {}

        // Send subscription to server

        return true;
      } else {
        this.hasPermission = false;
        store.dispatch(updateActiveNotifications("denied"));
        return false;
      }
    } catch (error) {
      console.error("Error initializing native push notifications:", error);
      store.dispatch(updateActiveNotifications("denied"));
      return false;
    }
  }

  /**
   * Initialize PWA push notifications using service workers
   */
  async initializePWA() {
    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (!registration) {
        console.error("Service Worker registration not found");
        return false;
      }

      // Check permission state
      const permissionState = await registration.pushManager.permissionState({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
      });

      if (permissionState === "granted") {
        this.hasPermission = true;

        // Get existing subscription or create new one
        let subscription = await registration.pushManager.getSubscription();

        if (!subscription) {
          subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
          });
        }

        this.subscription = subscription;

        // Send subscription to server
        if (!store.getState().user.postNotificationsSubscription.isLoading) {
          store.dispatch(postNotificationsSubscription(subscription));
        }

        store.dispatch(updateActiveNotifications("granted"));
        return true;
      } else {
        this.hasPermission = false;
        store.dispatch(updateActiveNotifications(permissionState));
        return false;
      }
    } catch (error) {
      console.error("Error initializing PWA push notifications:", error);
      store.dispatch(updateActiveNotifications("denied"));
      return false;
    }
  }

  /**
   * Set up native push notification listeners
   */
  async setupNativeListeners() {
    // Handle registration success

    if (this.listeners.length > 0) {
      return;
    }

    const tokenListener = await PushNotifications.addListener(
      "registration",
      (token) => {
        // alert("token generated", token);

        // Create a subscription object compatible with existing backend
        // but simplified - just the token without URL
        const subscription = {
          endpoint: token.value,
          keys: {
            p256dh: token.value,
            auth: token.value,
          },
        };

        this.subscription = subscription;

        // Send subscription to server (legacy endpoint)
        if (!store.getState().user.postNotificationsSubscription.isLoading) {
          store.dispatch(postNotificationsSubscription(subscription));
        }

        // Also sync with v1 device API (handles create-if-missing + token refresh)
        const currentProvider =
          store.getState().profilePushNotifications?.providerDeviceId;
        if (token.value !== currentProvider) {
          store.dispatch(
            syncPushNotificationDeviceAndTopics({
              providerDeviceId: token.value,
            })
          );
        }
      }
    );

    // Handle registration error
    // PushNotifications.addListener("registrationError", (error) => {
    //   console.error("Error on registration:", error.error);
    //   store.dispatch(updateActiveNotifications("denied"));
    // });

    // Handle push notification received (foreground)
    const notificationReceivedListener = await PushNotifications.addListener(
      "pushNotificationReceived",
      (event) => {
        const data = event?.data || {};

        // Silent subscription-status push (the backend sends this on ANY
        // subscription change — e.g. a CRM-side activation). Refetch the profile
        // — getUserInfo's change detection unlocks the menu/recipe gates in place
        // — and show nothing (no badge bump, no popup/banner).
        if (isSubscriptionUpdatePush(data)) {
          store.dispatch(refreshProfileAfterActivation());
          return;
        }

        // Increment unread badge
        store.dispatch(incrementRecipientsUnreadCount());
        store.dispatch(fetchUnreadCount());

        // Stream Chat push: the OS already displays it (FCM payload has a
        // notification block) and tapping it navigates via
        // pushNotificationActionPerformed. Show nothing in-app.
        if (data.sender === "stream.chat") {
          // This listener only fires in the foreground. If the message is for the
          // chat the user is currently viewing, suppress the OS banner — they're
          // already reading it. The system may post the notification a beat after
          // this event, so retry a couple of times.
          const cid = cidFromData(data);
          if (cid && window.__dsActiveChatCid === cid) {
            clearDeliveredChatNotifications(cid);
            setTimeout(() => clearDeliveredChatNotifications(cid), 400);
            setTimeout(() => clearDeliveredChatNotifications(cid), 1200);
          }
          return;
        }

        const isWithoutPopup =
          data.is_without_popup === true ||
          data.is_without_popup === "true" ||
          data.is_without_popup === "1";

        if (!isWithoutPopup) {
          // Build a recipient-like object from push data for the popup
          const popupNotification = {
            id: data.recipient_id || data.notification_id || Date.now(),
            deliver_id: data.deliver_id || null,
            attributes: {
              source: "topic",
              sent_at: new Date().toISOString(),
              read_at: null,
            },
            template: {
              attributes: {
                title: event?.title || data.title || "",
                body: event?.body || data.body || "",
                cover_url: data.cover_url || data.cover_image || null,
                is_without_popup: false,
                actions: (() => {
                  try {
                    const raw = data.actions;
                    if (typeof raw === "string") return JSON.parse(raw);
                    if (Array.isArray(raw)) return raw;
                    return [];
                  } catch {
                    return [];
                  }
                })(),
              },
            },
          };
          store.dispatch(showPopup(popupNotification));
        } else {
          // Personal interaction (new comment / new follower / mention): show a
          // brief top banner instead of the central popup. Tapping it deep-links
          // — comment → the post in Community (comment focused), follower → that
          // user's profile — reusing the same path logic as a background tap.
          // link_url is the backend's explicit destination and always wins:
          // a group post opens the feed at the post, a new follower opens their
          // profile, a follow REQUEST opens your own Contatti > Follower list
          // (the only screen that can accept it). follow_user_id is just the
          // fallback for payloads that carry no link — it used to take priority,
          // which sent every follow request to the requester's profile instead,
          // where there is nothing to accept.
          const communityLink =
            data.link_url ||
            (data.follow_user_id ? `/u/${data.follow_user_id}` : null);
          const path = communityLink
            ? pushLinkToInternalPath(communityLink)
            : null;
          window.dispatchEvent(
            new CustomEvent("push:banner", {
              detail: {
                title: event?.title || data.title || "",
                body: event?.body || data.body || "",
                path,
                // Actor identity, so the banner can show who this is about
                // instead of a bare line of text. cover_url is the actor's
                // avatar for personal pushes (comment / mention / follower).
                avatar: data.cover_url || null,
                actorName: data.actor_name || null,
              },
            })
          );
        }
      }
    );

    PushNotifications.addListener("registrationError", () => {});

    // Handle push notification action clicked (background/killed tap)
    const notificationActionListener = await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (event) => {
        try {
          const data = event?.notification?.data || {};

          // Silent subscription-status push opened/tapped → refetch + resync the
          // gates in place, no navigation.
          if (isSubscriptionUpdatePush(data)) {
            store.dispatch(refreshProfileAfterActivation());
            return;
          }

          // Stream Chat push (sent directly by Stream, no app backend): navigate
          // straight to the channel from its cid/channel_id.
          if (data.sender === "stream.chat") {
            const cid =
              data.cid ||
              (data.channel_type && data.channel_id
                ? `${data.channel_type}:${data.channel_id}`
                : data.channel_id);
            if (cid) {
              const path = `/chat?cid=${encodeURIComponent(cid)}`;
              // Buffer first (survives cold start), then notify any live listener.
              setPendingPushNavigation(path);
              window.dispatchEvent(
                new CustomEvent("push:navigate", { detail: { path } })
              );
            }
            return;
          }

          const isWithoutPopup =
            data.is_without_popup === true ||
            data.is_without_popup === "true" ||
            data.is_without_popup === "1";

          // Parse actions from push data
          let actions = [];
          try {
            const raw = data.actions;
            if (typeof raw === "string") actions = JSON.parse(raw);
            else if (Array.isArray(raw)) actions = raw;
          } catch {
            // silent
          }

          // Community notifications (new post, comment, @mention, new follower)
          // carry a deep-link in link_url / follow_user_id (or the primary
          // action). Tap = redirect straight to the target, mirroring the working
          // Centro-notifiche navigation, instead of a dead-end popup. Rich /
          // challenge pushes (no link_url) fall through to their popup path below.
          //
          // Same precedence as the foreground banner above: the backend's
          // link_url is authoritative, follow_user_id only fills in when there
          // is no link at all.
          const communityLink =
            data.link_url ||
            (data.follow_user_id ? `/u/${data.follow_user_id}` : null);
          if (communityLink) {
            // A link to somebody else's site is not a router path — routing on
            // its pathname alone would land the user on a blank in-app screen.
            if (isExternalActionUrl(communityLink)) {
              openExternalUrl(communityLink);
              return;
            }

            const path = pushLinkToInternalPath(communityLink);
            setPendingPushNavigation(path);
            window.dispatchEvent(
              new CustomEvent("push:navigate", { detail: { path } })
            );
            return;
          }

          if (!isWithoutPopup) {
            // Popup notification → show the popup modal
            const popupNotification = {
              id: data.recipient_id || data.notification_id || Date.now(),
              deliver_id: data.deliver_id || null,
              attributes: {
                source: "topic",
                sent_at: new Date().toISOString(),
                read_at: null,
              },
              template: {
                attributes: {
                  title: event?.notification?.title || data.title || "",
                  body: event?.notification?.body || data.body || "",
                  cover_url: data.cover_url || data.cover_image || null,
                  is_without_popup: false,
                  actions,
                },
              },
            };
            store.dispatch(showPopup(popupNotification));
          } else {
            // Without popup → mark as read via deliver_id and execute primary action
            if (data.deliver_id) {
              store.dispatch(markRecipientsReadByDelivery([data.deliver_id]));
            }
            const primaryAction = actions[0];
            if (primaryAction) {
              const attrs = primaryAction.attributes || primaryAction;
              const { url, verb, payload } = attrs;
              if (!url) {
                // No primary action target → nothing to do
              } else {
                const isAbsoluteUrl = /^https?:\/\//i.test(url);
                if (!isAbsoluteUrl) {
                  // Relative URL → in-app navigation. Backend has been seen
                  // sending `verb: "GET"` alongside deep-link paths like
                  // `/dirette/4`; the verb must NOT trigger a fetch in that
                  // case — it would just hit the WebView's own static HTML.
                  window.dispatchEvent(
                    new CustomEvent("push:navigate", { detail: { path: url } })
                  );
                  setPendingPushNavigation(url);
                } else if (verb) {
                  // Absolute URL + verb → backend API call (fire and forget).
                  fetch(url, {
                    method: verb,
                    headers: {
                      "Content-Type": "application/json",
                      Accept: "application/json",
                      Authorization: `Bearer ${getAuthToken()}`,
                    },
                    body: verb !== "GET" ? JSON.stringify(payload) : undefined,
                  }).catch(() => {});
                } else {
                  // Absolute URL, no verb → treat as a deep link the router
                  // can match (strip origin to a path). Avoids accidentally
                  // opening external sites inside the WebView.
                  let path = url;
                  try {
                    const u = new URL(url);
                    path = `${u.pathname}${u.search}${u.hash}`;
                  } catch {
                    // already an in-app path
                  }
                  window.dispatchEvent(
                    new CustomEvent("push:navigate", { detail: { path } })
                  );
                  setPendingPushNavigation(path);
                }
              }
            }
            // No primary action → do nothing
          }
        } catch (err) {
          console.warn("Push navigation error:", err);
        }
      }
    );

    // Save listeners into the array for cleanup/tracking
    this.listeners.push(
      tokenListener,
      notificationReceivedListener,
      notificationActionListener
    );

  }

  /**
   * Request permission for push notifications
   */
  async requestPermission() {
    if (this.platform === "native") {
      return this.initializeNative({ prompt: true });
    } else if (this.platform === "pwa") {
      return this.initializePWA();
    }
    return false;
  }

  /**
   * Check current permission state without requesting permissions
   */
  async checkPermissionState() {
    if (this.platform === "native") {
      try {
        const permissionState = await PushNotifications.checkPermissions();
        return permissionState.receive;
      } catch (error) {
        console.error("Error checking native permission state:", error);
        return "denied";
      }
    } else if (this.platform === "pwa") {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return "denied";

        const permissionState = await registration.pushManager.permissionState({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(PUBLIC_KEY),
        });
        return permissionState;
      } catch (error) {
        console.error("Error checking PWA permission state:", error);
        return "denied";
      }
    }
    return "denied";
  }

  /**
   * Get current subscription
   */
  async getSubscription() {
    if (this.platform === "native") {
      // For native, return the FCM token object
      return this.subscription;
    } else if (this.platform === "pwa") {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return null;

        return await registration.pushManager.getSubscription();
      } catch (error) {
        console.error("Error getting PWA subscription:", error);
        return null;
      }
    }
    return null;
  }

  /**
   * Unsubscribe from push notifications
   */
  async unsubscribe() {
    if (this.platform === "native") {
      try {
        // await PushNotifications.removeAllListeners();
        this.subscription = null;
        this.hasPermission = false;
        store.dispatch(updateActiveNotifications("denied"));
        return true;
      } catch (error) {
        console.error("Error unsubscribing from native notifications:", error);
        return false;
      }
    } else if (this.platform === "pwa") {
      try {
        const registration = await navigator.serviceWorker.getRegistration();
        if (!registration) return false;

        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
        }

        this.subscription = null;
        this.hasPermission = false;
        store.dispatch(updateActiveNotifications("denied"));
        return true;
      } catch (error) {
        console.error("Error unsubscribing from PWA notifications:", error);
        return false;
      }
    }
    return false;
  }

  /**
   * Check if push notifications are supported
   */
  isSupported() {
    return this.platform === "native" || this.platform === "pwa";
  }

  /**
   * Get platform type
   */
  getPlatform() {
    return this.platform;
  }
}

// Create singleton instance
const pushNotificationService = new PushNotificationService();

export default pushNotificationService;
